import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { getApi } from "../../../api/api";
import { OrderInterface } from "../../../common/types/interfaces";
import { useScrollPagination } from "../../../common/hooks/useScrollPagination";

const PAGE_LIMIT = 20;

export const useOrder = () => {
  const [searchParams] = useSearchParams();
  const clusterIdParam = searchParams.get("clusterId");
  const statusParam = searchParams.get("status");
  const clusterId = clusterIdParam ? Number(clusterIdParam) : undefined;
  const status = statusParam ? Number(statusParam) : undefined;

  const [orders, setOrders] = useState<OrderInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    setOrders([]);
    setLoading(true);
  }, [clusterId, status]);

  useEffect(() => {
    if (!hasMore && offset > 0) return;
    if (isFetching || isFetchingMore) return;

    let cancelled = false;

    if (offset === 0) setIsFetching(true);
    else setIsFetchingMore(true);

    getApi()
      .getOrders({
        limit: PAGE_LIMIT,
        offset,
        clusterId,
        statuses: status ? [status] : undefined,
      })
      .then((res) => {
        if (!cancelled) {
          const data = Array.isArray(res) ? res : [];
          setOrders((prev) => (offset === 0 ? data : [...prev, ...data]));
          if (data.length < PAGE_LIMIT) setHasMore(false);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          if (offset === 0) setOrders([]);
          setHasMore(false);
          setLoading(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
          setIsFetchingMore(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId, status, offset]);

  const loadMore = useCallback(() => {
    if (!isFetching && !isFetchingMore && hasMore) {
      setOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [isFetching, isFetchingMore, hasMore]);

  const { lastItemRef } = useScrollPagination(
    hasMore,
    isFetchingMore,
    loadMore,
  );

  return {
    orders,
    clusterId,
    status,
    loading,
    ready: !loading,
    isFetching,
    isFetchingMore,
    hasMore,
    loadMore,
    lastItemRef,
    isEmpty: !loading && orders.length === 0,
  };
};